import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  resolveShapeToolbarPlacement,
  SHAPE_TOOLBAR_FALLBACK_SIZE,
  SHAPE_TOOLBAR_GAP_ABOVE,
  type Point,
  type Size,
} from './shapeToolbarPlacement';

type SelectionBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type StageTransform = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

type UseShapeToolbarPlacementOptions = {
  selectionBounds: SelectionBounds;
  stage: StageTransform;
  dragOffset: Point;
  isDragging: boolean;
  onForgetDragOffset: () => void;
};

function rawToolbarPlacement(anchorCenterX: number, anchorTopY: number, dragOffset: Point) {
  return {
    centerX: anchorCenterX + dragOffset.x,
    topY: anchorTopY + dragOffset.y,
    forgotDragOffset: false,
  };
}

export function useShapeToolbarPlacement({
  selectionBounds,
  stage,
  dragOffset,
  isDragging,
  onForgetDragOffset,
}: UseShapeToolbarPlacementOptions) {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [toolbarSize, setToolbarSize] = useState<Size>(SHAPE_TOOLBAR_FALLBACK_SIZE);
  const [viewportSize, setViewportSize] = useState<Size>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = toolbarRef.current;
    if (!element) return;

    const container = element.offsetParent as HTMLElement | null;

    const updateMeasurements = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setToolbarSize({ width: rect.width, height: rect.height });
      }

      if (container) {
        const containerRect = container.getBoundingClientRect();
        if (containerRect.width > 0 && containerRect.height > 0) {
          setViewportSize({ width: containerRect.width, height: containerRect.height });
        }
      }
    };

    updateMeasurements();

    const handleWindowResize = () => updateMeasurements();
    window.addEventListener('resize', handleWindowResize);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateMeasurements);
      resizeObserver.observe(element);
      if (container) resizeObserver.observe(container);
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      resizeObserver?.disconnect();
    };
  }, []);

  const placement = useMemo(() => {
    const anchorScreenX = selectionBounds.x * stage.scale + stage.offsetX;
    const anchorScreenY = selectionBounds.y * stage.scale + stage.offsetY;
    const widthScreen = selectionBounds.width * stage.scale;
    const anchorCenterX = anchorScreenX + widthScreen / 2;
    const anchorTopY = anchorScreenY - SHAPE_TOOLBAR_GAP_ABOVE;

    if (isDragging) {
      return rawToolbarPlacement(anchorCenterX, anchorTopY, dragOffset);
    }

    if (viewportSize.width <= 0 || viewportSize.height <= 0) {
      return rawToolbarPlacement(anchorCenterX, anchorTopY, dragOffset);
    }

    return resolveShapeToolbarPlacement({
      anchorCenterX,
      anchorTopY,
      dragOffset,
      toolbarSize,
      viewport: viewportSize,
    });
  }, [
    dragOffset,
    isDragging,
    selectionBounds.height,
    selectionBounds.width,
    selectionBounds.x,
    selectionBounds.y,
    stage.offsetX,
    stage.offsetY,
    stage.scale,
    toolbarSize,
    viewportSize,
  ]);

  useLayoutEffect(() => {
    if (isDragging || !placement.forgotDragOffset) return;
    onForgetDragOffset();
  }, [isDragging, onForgetDragOffset, placement.forgotDragOffset]);

  return { toolbarRef, placement };
};
